/**
 * Helpers for Manifest V2 leg shipping fields on create/edit forms.
 */

import { isDispatchBuildTripEnabled } from "./dispatchBuildTrip";

export const HAZMAT_FIELD_LABELS = {
  unNumber: "UN number",
  properShippingName: "Proper shipping name",
  hazardClass: "Hazard class",
  packingGroup: "Packing group",
  emergencyContact: "Emergency contact",
  emergencyPhone: "Emergency phone",
};

/**
 * Normalize shipping object from leg form state for API payload.
 *
 * @param {object} [shipping]
 * @returns {object|undefined}
 */
export function buildLegShippingPayload(shipping = {}) {
  if (!shipping || typeof shipping !== "object") {
    return undefined;
  }

  const refs = shipping.customerReferenceNumbers;
  const normalizedRefs = Array.isArray(refs)
    ? refs.filter(Boolean)
    : String(refs || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

  const payload = {
    bolNumber: shipping.bolNumber || "",
    proNumber: shipping.proNumber || "",
    commodity: shipping.commodity || "",
    description: shipping.description || "",
    pieces:
      shipping.pieces === "" || shipping.pieces == null
        ? undefined
        : Number(shipping.pieces),
    shipperReference: shipping.shipperReference || "",
    consigneeReference: shipping.consigneeReference || "",
    customerReferenceNumbers: normalizedRefs,
    isHazmat: Boolean(shipping.isHazmat),
    temperatureRequired: Boolean(shipping.temperatureRequired),
  };

  if (shipping.isHazmat) {
    payload.hazmat = {
      unNumber: shipping.hazmat?.unNumber || "",
      properShippingName: shipping.hazmat?.properShippingName || "",
      hazardClass: shipping.hazmat?.hazardClass || "",
      packingGroup: shipping.hazmat?.packingGroup || "",
      emergencyContact: shipping.hazmat?.emergencyContact || "",
      emergencyPhone: shipping.hazmat?.emergencyPhone || "",
    };
  }

  if (shipping.temperatureRequired) {
    const min =
      shipping.temperatureRange?.min === "" || shipping.temperatureRange?.min == null
        ? null
        : Number(shipping.temperatureRange.min);
    const max =
      shipping.temperatureRange?.max === "" || shipping.temperatureRange?.max == null
        ? null
        : Number(shipping.temperatureRange.max);

    payload.temperatureRange = {
      min,
      max,
      unit: shipping.temperatureRange?.unit || "F",
    };
  }

  return payload;
}

/**
 * Map leg record shipping into form-friendly shape.
 *
 * @param {object} [leg]
 * @returns {object}
 */
export function mapLegShippingToForm(leg = {}) {
  const shipping = leg.shipping || {};
  const refs = shipping.customerReferenceNumbers;

  return {
    bolNumber: shipping.bolNumber || "",
    proNumber: shipping.proNumber || "",
    commodity: shipping.commodity || "",
    description: shipping.description || "",
    pieces: shipping.pieces ?? "",
    shipperReference: shipping.shipperReference || "",
    consigneeReference: shipping.consigneeReference || "",
    customerReferenceNumbers: Array.isArray(refs) ? refs.join(", ") : refs || "",
    isHazmat: Boolean(shipping.isHazmat),
    hazmat: {
      unNumber: shipping.hazmat?.unNumber || "",
      properShippingName: shipping.hazmat?.properShippingName || "",
      hazardClass: shipping.hazmat?.hazardClass || "",
      packingGroup: shipping.hazmat?.packingGroup || "",
      emergencyContact: shipping.hazmat?.emergencyContact || "",
      emergencyPhone: shipping.hazmat?.emergencyPhone || "",
    },
    temperatureRequired: Boolean(shipping.temperatureRequired),
    temperatureRange: {
      min: shipping.temperatureRange?.min ?? "",
      max: shipping.temperatureRange?.max ?? "",
      unit: shipping.temperatureRange?.unit || "F",
    },
  };
}

/**
 * Shipping & compliance on the leg form. Same opt-in as Build trip:
 * both `manifests.v2.enabled` and `dispatch.buildTrip.enabled` must be true.
 *
 * @param {object} [configs]
 * @returns {boolean}
 */
export function isManifestV2ShippingEnabled(configs = {}) {
  return isDispatchBuildTripEnabled(configs);
}

/**
 * Shipping + complianceProfile for create/update only when the trip-manifest
 * flags are on. When they are off, omit the keys so v1 saves stay unchanged.
 *
 * @param {object} [formValues]
 * @param {object} [configs]
 * @returns {{ shipping?: object, complianceProfile?: string }}
 */
export function buildOptionalLegShippingFields(formValues = {}, configs = {}) {
  if (!isManifestV2ShippingEnabled(configs)) {
    return {};
  }

  const shippingPayload = buildLegShippingPayload(formValues.shipping);
  return {
    ...(shippingPayload ? { shipping: shippingPayload } : {}),
    ...(formValues.complianceProfile
      ? { complianceProfile: formValues.complianceProfile }
      : {}),
  };
}
